package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * What a route change looks like, in one place.
 *
 * Navigating inside the app replaced the whole content area instantly: `/app/projects` to
 * `/app/projects/new` swapped one screenful for another with nothing between them, which reads as a
 * page reload rather than as a step in a flow. Clicking **Log in** did the same.
 *
 * This is deliberately the *only* route-level motion in the product, for the same reason the
 * marketing page allows itself exactly one self-starting animation: consistency here is worth more
 * than expressiveness. Every route enters the same way, at the same speed, so the movement stops
 * being something a reader notices and becomes something that tells them the view changed.
 *
 * **Transform only, and 6px.** No opacity — a keyframe that starts transparent strands its content
 * invisible in a tab the browser has stopped painting, and the base style must always be the
 * finished, readable state. Six pixels rather than [LReveal]'s eighteen: a reveal is a flourish on
 * arrival, a route change is navigation, and a whole screen sliding that far reads as a carousel.
 *
 * **[Motion.StandardMs], not slower.** A route change sits directly under a click, and anything past
 * ~200ms there is felt as the app thinking rather than as the app responding.
 *
 * `prefers-reduced-motion` needs no handling here. The global rule in `initLingvoTheme` neutralises
 * duration *and* delay, so the content simply appears where it was always going to be.
 */
@Composable
fun LRouteTransition(
    routeKey: String,
    content: @Composable () -> Unit,
) {
    // `key` is what makes this work at all: it forces Compose to discard the previous route's node
    // and mount a fresh one, so the animation actually re-runs. Without it Compose reuses the
    // subtree, the element never re-enters, and the transition plays exactly once — on first load,
    // which is the one time it is least wanted.
    //
    // **And the first route drawn still is that time.** A cold load of `/login` is a mount like any
    // other, so the entrance played over content the visitor had not asked to see move — on a public
    // page, outside the marketing motion budget entirely. The first route to be drawn now arrives
    // already in place; every route after it is a transition from something, which is the only case
    // this component was built for.
    var drawnOnce by remember { mutableStateOf(false) }
    key(routeKey) {
        // Remembered *inside* the key, so the decision is made once per route and cannot flip
        // halfway through: a later recomposition recalculating it would strip the animation off an
        // element mid-flight.
        val entering = remember { drawnOnce }
        Div(
            if (entering) {
                LRouteTransitionStyle.toModifier().toAttrs()
            } else {
                Modifier.fillMaxWidth().toAttrs()
            },
        ) { content() }
    }
    SideEffect { drawnOnce = true }
}

/**
 * Six pixels up, and nothing else.
 *
 * The direction is deliberate: content rising slightly reads as arriving, and the same distance
 * downward reads as the page settling after a load, which is a different message.
 */
val LRouteEnter = Keyframes {
    from { Modifier.translateY(6.px) }
    to { Modifier.translateY(0.px) }
}

val LRouteTransitionStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .animation(
            LRouteEnter.toAnimation(
                duration = Motion.StandardMs.ms,
                timingFunction = Motion.EaseOutTiming,
                // Backwards, so a route that mounts behind a suspense boundary holds its start
                // frame rather than snapping into place when it resolves.
                fillMode = AnimationFillMode.Backwards,
            ),
        )
}
